import React, { useState } from 'react';
import styled from 'styled-components';
import { Radio } from 'components/FormFields';

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const DemoCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
`;

const DemoRow = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DemoColumn = styled.div`
  flex: 1;
  min-width: 250px;
`;

const DemoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : '#f8fafc'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.4)' : '#1e293b'};
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  margin: 0 0 24px 0;
  font-size: 13px;
  line-height: 1.6;
  color: #e2e8f0;
`;

const PropsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  font-size: 14px;

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid
      ${(props) =>
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  }

  th {
    font-weight: 600;
    color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'};
  }

  td {
    color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  }

  code {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const RadioDocs = () => {
  const [basicValue, setBasicValue] = useState('option1');
  const [inlineValue, setInlineValue] = useState('red');
  const [statusValue, setStatusValue] = useState('active');
  const [sizeValue, setSizeValue] = useState('medium');

  const basicOptions = [
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' },
  ];

  const colorOptions = [
    { label: 'Red', value: 'red' },
    { label: 'Blue', value: 'blue' },
    { label: 'Green', value: 'green' },
    { label: 'Yellow', value: 'yellow' },
  ];

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Pending', value: 'pending' },
  ];

  const sizeOptions = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Extra Large (Disabled)', value: 'xlarge', disabled: true },
  ];

  return (
    <div>
      <PageTitle>Radio</PageTitle>
      <PageDescription>
        A themed radio button group component for single selection from multiple
        options. Supports vertical and inline layouts, disabled states, and
        helper text.
      </PageDescription>

      {/* Basic Usage */}
      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          Import the Radio component and provide options with labels and values.
        </SectionDescription>
        <CodeBlock>
          {`import { Radio } from 'components/FormFields';

const [value, setValue] = useState('option1');

<Radio
  name="example"
  label="Select an option"
  value={value}
  options={[
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' },
  ]}
  onChange={(e) => setValue(e.target.value)}
/>`}
        </CodeBlock>
      </Section>

      {/* Vertical Layout */}
      <Section>
        <SectionTitle>Vertical Layout (Default)</SectionTitle>
        <SectionDescription>
          By default, radio options are displayed vertically (stacked).
        </SectionDescription>
        <DemoCard>
          <DemoRow>
            <DemoColumn>
              <DemoLabel>Vertical Radio Group</DemoLabel>
              <Radio
                name="basic"
                label="Choose your preference"
                value={basicValue}
                options={basicOptions}
                onChange={(e) => setBasicValue(e.target.value)}
              />
              <SelectedValue>Selected: {basicValue}</SelectedValue>
            </DemoColumn>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Inline Layout */}
      <Section>
        <SectionTitle>Inline Layout</SectionTitle>
        <SectionDescription>
          Use the <code>inline</code> prop to display options horizontally.
        </SectionDescription>
        <DemoCard>
          <DemoRow>
            <DemoColumn>
              <DemoLabel>Inline Radio Group</DemoLabel>
              <Radio
                name="colors"
                label="Select a color"
                value={inlineValue}
                options={colorOptions}
                onChange={(e) => setInlineValue(e.target.value)}
                inline
              />
              <SelectedValue>Selected: {inlineValue}</SelectedValue>
            </DemoColumn>
          </DemoRow>
        </DemoCard>
        <CodeBlock>
          {`<Radio
  name="colors"
  label="Select a color"
  value={value}
  options={colorOptions}
  onChange={(e) => setValue(e.target.value)}
  inline
/>`}
        </CodeBlock>
      </Section>

      {/* With Helper Text */}
      <Section>
        <SectionTitle>With Helper Text</SectionTitle>
        <SectionDescription>
          Add helper text below the radio group for additional context.
        </SectionDescription>
        <DemoCard>
          <DemoRow>
            <DemoColumn>
              <DemoLabel>With Helper Text</DemoLabel>
              <Radio
                name="status"
                label="Status"
                value={statusValue}
                options={statusOptions}
                onChange={(e) => setStatusValue(e.target.value)}
                helperText="Select the current status of the item"
              />
              <SelectedValue>Selected: {statusValue}</SelectedValue>
            </DemoColumn>
            <DemoColumn>
              <DemoLabel>With Error Helper</DemoLabel>
              <Radio
                name="status-error"
                label="Status"
                value=""
                options={statusOptions}
                helperText="Please select a status"
                error
              />
            </DemoColumn>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Disabled States */}
      <Section>
        <SectionTitle>Disabled States</SectionTitle>
        <SectionDescription>
          Disable the entire group or individual options.
        </SectionDescription>
        <DemoCard>
          <DemoRow>
            <DemoColumn>
              <DemoLabel>Individual Option Disabled</DemoLabel>
              <Radio
                name="sizes"
                label="Select a size"
                value={sizeValue}
                options={sizeOptions}
                onChange={(e) => setSizeValue(e.target.value)}
              />
              <SelectedValue>Selected: {sizeValue}</SelectedValue>
            </DemoColumn>
            <DemoColumn>
              <DemoLabel>Entire Group Disabled</DemoLabel>
              <Radio
                name="disabled-group"
                label="Select an option"
                value="option1"
                options={basicOptions}
                disabled
              />
            </DemoColumn>
          </DemoRow>
        </DemoCard>
        <CodeBlock>
          {`// Individual option disabled
const options = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large (Disabled)', value: 'large', disabled: true },
];

// Entire group disabled
<Radio
  name="disabled-group"
  label="Select an option"
  value={value}
  options={options}
  disabled
/>`}
        </CodeBlock>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable>
          <thead>
            <tr>
              <th>Prop</th>
              <th>Type</th>
              <th>Default</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>name</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>-</td>
              <td>Name attribute for the radio group (required)</td>
            </tr>
            <tr>
              <td>
                <code>label</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>-</td>
              <td>Label displayed above the radio group</td>
            </tr>
            <tr>
              <td>
                <code>value</code>
              </td>
              <td>
                <code>string | number</code>
              </td>
              <td>-</td>
              <td>Currently selected value</td>
            </tr>
            <tr>
              <td>
                <code>options</code>
              </td>
              <td>
                <code>Array</code>
              </td>
              <td>
                <code>[]</code>
              </td>
              <td>
                Array of options: <code>{'{label, value, disabled?}'}</code>
              </td>
            </tr>
            <tr>
              <td>
                <code>onChange</code>
              </td>
              <td>
                <code>Function</code>
              </td>
              <td>-</td>
              <td>
                Called when selection changes with{' '}
                <code>{'{target: {name, value}}'}</code>
              </td>
            </tr>
            <tr>
              <td>
                <code>inline</code>
              </td>
              <td>
                <code>boolean</code>
              </td>
              <td>
                <code>false</code>
              </td>
              <td>Display options horizontally</td>
            </tr>
            <tr>
              <td>
                <code>disabled</code>
              </td>
              <td>
                <code>boolean</code>
              </td>
              <td>
                <code>false</code>
              </td>
              <td>Disable all options</td>
            </tr>
            <tr>
              <td>
                <code>helperText</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>-</td>
              <td>Helper text displayed below the radio group</td>
            </tr>
            <tr>
              <td>
                <code>error</code>
              </td>
              <td>
                <code>boolean</code>
              </td>
              <td>
                <code>false</code>
              </td>
              <td>Show error state for helper text</td>
            </tr>
          </tbody>
        </PropsTable>
      </Section>

      {/* Usage with Forms */}
      <Section>
        <SectionTitle>Usage with Forms</SectionTitle>
        <CodeBlock>
          {`import { Radio } from 'components/FormFields';

const MyForm = () => {
  const [formData, setFormData] = useState({
    status: 'active',
    priority: 'medium',
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <form>
      <Radio
        name="status"
        label="Status"
        value={formData.status}
        options={[
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' },
        ]}
        onChange={handleChange}
      />

      <Radio
        name="priority"
        label="Priority"
        value={formData.priority}
        options={[
          { label: 'Low', value: 'low' },
          { label: 'Medium', value: 'medium' },
          { label: 'High', value: 'high' },
        ]}
        onChange={handleChange}
        inline
      />
    </form>
  );
};`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default RadioDocs;
